/**
 * LiveSlidePreview Component
 *
 * Shows a slide mockup with character-by-character typing effect.
 * Displays title, subtitle, body text, and bullets as they stream in.
 */

import React from 'react';
import { TypingContent } from '../utils/streamParser';

interface LiveSlidePreviewProps {
  content: TypingContent;
  themeId?: string;
  themeColors?: {
    background: string;
    text: string;
    accent: string;
    headingFont?: string;
    bodyFont?: string;
    bulletStyle?: string;
  };
}

/**
 * Default theme colors (modern-light fallback)
 */
const DEFAULT_COLORS = {
  background: '#ffffff',
  text: '#1a1a1a',
  accent: '#426ab8',
};

/**
 * Theme color, typography, and bullet style mappings
 * Matches the 10 curated themes in include/themes/*.json
 */
const THEME_COLORS: Record<string, {
  background: string;
  text: string;
  accent: string;
  headingFont?: string;
  bodyFont?: string;
  bulletStyle?: string;
}> = {
  // 10 curated themes - colors from actual theme JSON files
  'launch': { background: '#0F172A', text: '#F1F5F9', accent: '#8B5CF6', headingFont: 'system-ui', bodyFont: 'system-ui', bulletStyle: 'disc' },
  'editorial': { background: '#FAF8F5', text: '#2D2D3A', accent: '#1A1A2E', headingFont: 'Playfair Display', bodyFont: 'Source Sans 3', bulletStyle: 'dash' },
  'impact': { background: '#0D0D0D', text: '#E0E0E0', accent: '#FF3366', headingFont: 'system-ui', bodyFont: 'system-ui', bulletStyle: 'square' },
  'refined': { background: '#F7F5F2', text: '#2C2C2C', accent: '#2C2C2C', headingFont: 'Cormorant Garamond', bodyFont: 'system-ui', bulletStyle: 'circle-outline' },
  'terminal': { background: '#0F172A', text: '#E2E8F0', accent: '#38BDF8', headingFont: 'JetBrains Mono', bodyFont: 'IBM Plex Sans', bulletStyle: 'chevron' },
  'warm': { background: '#FFFAF5', text: '#2D3436', accent: '#E17055', headingFont: 'system-ui', bodyFont: 'system-ui', bulletStyle: 'filled-circle' },
  'swiss': { background: '#FFFFFF', text: '#111111', accent: '#333333', headingFont: 'Helvetica Neue', bodyFont: 'Helvetica Neue', bulletStyle: 'number' },
  'phosphor': { background: '#0A0A0A', text: '#33FF33', accent: '#33FF33', headingFont: 'Fira Code', bodyFont: 'IBM Plex Mono', bulletStyle: 'chevron' },
  'coral-sunset': { background: '#D4455A', text: '#FFFFFF', accent: '#FFD700', headingFont: 'system-ui', bodyFont: 'system-ui', bulletStyle: 'filled-circle' },
  'minimal': { background: '#FFFFFF', text: '#333333', accent: '#333333', headingFont: 'system-ui', bodyFont: 'system-ui', bulletStyle: 'disc' },
  // Legacy themes (fallback)
  'modern-light': { background: '#ffffff', text: '#1a1a1a', accent: '#426ab8', bulletStyle: 'disc' },
  'modern-dark': { background: '#1a1a1a', text: '#ffffff', accent: '#60a5fa', bulletStyle: 'disc' },
  'startup': { background: '#0f0f1a', text: '#ffffff', accent: '#8b5cf6', bulletStyle: 'disc' },
};

/**
 * Get bullet character for a style
 */
const BULLET_CHARS: Record<string, string> = {
  'disc': '•',
  'filled-circle': '•',
  'circle-outline': '○',
  'square': '▪',
  'dash': '—',
  'chevron': '›',
  'number': '1.',
};

/**
 * Get theme colors and typography by ID
 */
export function getThemeColors(themeId?: string) {
  if (!themeId) return { ...DEFAULT_COLORS, headingFont: 'Inter', bodyFont: 'Inter', bulletStyle: 'disc' };
  const theme = THEME_COLORS[themeId];
  if (!theme) return { ...DEFAULT_COLORS, headingFont: 'Inter', bodyFont: 'Inter', bulletStyle: 'disc' };
  return {
    ...theme,
    headingFont: theme.headingFont || 'Inter',
    bodyFont: theme.bodyFont || 'Inter',
    bulletStyle: theme.bulletStyle || 'disc',
  };
}

/**
 * Get bullet character for theme
 */
export function getBulletChar(themeId?: string, index?: number): string {
  const theme = getThemeColors(themeId);
  const style = theme.bulletStyle || 'disc';
  if (style === 'number' && index !== undefined) {
    return `${index + 1}.`;
  }
  return BULLET_CHARS[style] || '•';
}

/**
 * Typing cursor component
 */
function TypingCursor({ visible }: { visible: boolean }) {
  if (!visible) return null;
  return <span className="typing-cursor">|</span>;
}

/**
 * Live slide preview with typing effect
 */
export function LiveSlidePreview({ content, themeId, themeColors }: LiveSlidePreviewProps) {
  const colors = themeColors || { ...DEFAULT_COLORS, headingFont: 'Inter', bodyFont: 'Inter', bulletStyle: 'disc' };
  const headingFont = colors.headingFont || 'Inter';
  const bodyFont = colors.bodyFont || 'Inter';
  const bulletStyle = colors.bulletStyle || 'disc';

  return (
    <div
      className="live-slide-preview"
      style={{
        backgroundColor: colors.background,
        color: colors.text,
        fontFamily: `"${bodyFont}", sans-serif`,
      }}
    >
      {/* Accent bar at top */}
      <div
        className="live-slide-accent"
        style={{ backgroundColor: colors.accent }}
      />

      <div className="live-slide-content">
        {/* Title */}
        {content.title && (
          <h1 className="live-slide-title" style={{ color: colors.text, fontFamily: `"${headingFont}", sans-serif` }}>
            {content.title}
            <TypingCursor visible={content.isTypingTitle} />
          </h1>
        )}

        {/* Subtitle */}
        {content.subtitle && (
          <h2 className="live-slide-subtitle" style={{ color: colors.text, opacity: 0.7, fontFamily: `"${bodyFont}", sans-serif` }}>
            {content.subtitle}
            <TypingCursor visible={content.isTypingSubtitle} />
          </h2>
        )}

        {/* Body text */}
        {content.bodyLines.length > 0 && (
          <div className="live-slide-body">
            {content.bodyLines.map((line, i) => (
              <p key={i} style={{ color: colors.text, opacity: 0.85 }}>
                {line}
                {content.isTypingBody && i === content.bodyLines.length - 1 && (
                  <TypingCursor visible={true} />
                )}
              </p>
            ))}
          </div>
        )}

        {/* Bullet list */}
        {content.bulletItems.length > 0 && (
          <ul className="live-slide-bullets" data-bullet-style={bulletStyle}>
            {content.bulletItems.map((item, i) => {
              const bulletChar = bulletStyle === 'number' ? `${i + 1}.` : (BULLET_CHARS[bulletStyle] || '•');
              return (
                <li key={i} style={{ color: colors.text }}>
                  <span className="bullet-char" style={{ color: colors.accent }}>
                    {bulletChar}
                  </span>
                  <span className="bullet-text">
                    {item}
                    {content.isTypingBullets && i === content.bulletItems.length - 1 && (
                      <TypingCursor visible={true} />
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}

        {/* Empty state - just started */}
        {!content.title && !content.subtitle && content.bodyLines.length === 0 && (
          <div className="live-slide-empty">
            <span className="typing-cursor">|</span>
          </div>
        )}
      </div>

      {/* Screen type badge */}
      {content.screenType && (
        <div className="live-slide-type-badge">
          {formatScreenType(content.screenType)}
        </div>
      )}
    </div>
  );
}

/**
 * Format screen type for display
 */
function formatScreenType(type: string): string {
  const labels: Record<string, string> = {
    'title-slide': 'Title',
    'section-header': 'Section',
    'content': 'Content',
    'two-column': 'Two Column',
    'image-focus': 'Image',
    'quote-slide': 'Quote',
    'closing': 'Closing',
  };
  return labels[type] || type;
}

/**
 * Theme Chrome - wraps the slide preview with theme-specific frame/decoration
 */
interface ThemeChromeProps {
  themeId?: string;
  children: React.ReactNode;
}

export function ThemeChrome({ themeId, children }: ThemeChromeProps) {
  const id = themeId || 'launch';

  switch (id) {
    case 'phosphor':
      // Full CRT monitor aesthetic
      return (
        <div className="theme-chrome theme-chrome-phosphor">
          <div className="crt-bezel">
            <div className="crt-brand">FLOW-2000</div>
            <div className="crt-screen">
              <div className="crt-scanlines" />
              <div className="crt-glow" />
              {children}
            </div>
            <div className="crt-controls">
              <div className="crt-knob" />
              <div className="crt-knob" />
              <div className="crt-led" />
            </div>
          </div>
        </div>
      );

    case 'terminal':
      // Terminal window chrome
      return (
        <div className="theme-chrome theme-chrome-terminal">
          <div className="terminal-titlebar">
            <div className="terminal-buttons">
              <span className="terminal-btn red" />
              <span className="terminal-btn yellow" />
              <span className="terminal-btn green" />
            </div>
            <div className="terminal-title">flow2: generating</div>
          </div>
          <div className="terminal-content">
            {children}
          </div>
        </div>
      );

    case 'editorial':
      // Elegant magazine frame
      return (
        <div className="theme-chrome theme-chrome-editorial">
          <div className="editorial-frame">
            <div className="editorial-corner tl" />
            <div className="editorial-corner tr" />
            <div className="editorial-corner bl" />
            <div className="editorial-corner br" />
            {children}
          </div>
        </div>
      );

    case 'impact':
      // Bold frame with pulse
      return (
        <div className="theme-chrome theme-chrome-impact">
          <div className="impact-frame">
            <div className="impact-pulse" />
            {children}
          </div>
        </div>
      );

    case 'refined':
      // Thin elegant brass frame
      return (
        <div className="theme-chrome theme-chrome-refined">
          <div className="refined-frame">
            {children}
          </div>
        </div>
      );

    case 'warm':
      // Soft rounded friendly frame
      return (
        <div className="theme-chrome theme-chrome-warm">
          <div className="warm-frame">
            {children}
          </div>
        </div>
      );

    case 'swiss':
      // Minimal hairline border
      return (
        <div className="theme-chrome theme-chrome-swiss">
          {children}
        </div>
      );

    case 'coral-sunset':
      // Gradient border frame
      return (
        <div className="theme-chrome theme-chrome-coral">
          <div className="coral-gradient-frame">
            {children}
          </div>
        </div>
      );

    case 'minimal':
      // Almost invisible - just subtle shadow
      return (
        <div className="theme-chrome theme-chrome-minimal">
          {children}
        </div>
      );

    case 'launch':
    default:
      // Clean modern dark bezel with accent glow
      return (
        <div className="theme-chrome theme-chrome-launch">
          <div className="launch-bezel">
            <div className="launch-notch" />
            <div className="launch-glow" />
            {children}
          </div>
        </div>
      );
  }
}

export default LiveSlidePreview;
